<template>
  <AdaptiveSheet v-model:opened="opened">
    <f7-page v-if="local">
      <f7-navbar :title="$t('payout.detail')">
        <f7-nav-right>
            <f7-link sheet-close popup-close><i class="icon icon-close"></i></f7-link>
          </f7-nav-right>
      </f7-navbar>

      <f7-block class="amount-block">
        <div class="amount-value">{{ fmtAmount(local) }}</div>
        <div class="amount-recipient">{{ $t('payout.for', { name: local.recipientName }) }}</div>
        <PayoutStatusPill :status="local.status" />
      </f7-block>

      <f7-block class="reason-block">
        <div class="reason-label">{{ $t('payout.reason') }}</div>
        <div class="reason-text">{{ local.reason || '—' }}</div>
        <div class="reason-meta">
          {{ $t('payout.initiatedBy', { name: local.initiatedByName, time: relativeTime(local.createdAt) }) }}
        </div>
      </f7-block>

      <f7-block-title>
        {{ $t('payout.approvalProgress', {
          current: local.currentApprovalCount,
          required: local.requiredApprovalCount,
        }) }}
      </f7-block-title>
      <f7-block class="progress-block">
        <div class="progress-track">
          <div class="progress-fill" :style="{ width: progressPct + '%' }" />
        </div>
      </f7-block>

      <f7-block class="approval-list">
        <PayoutApprovalRow
          v-for="a in local.approvals"
          :key="a.approverId"
          :approval="a"
          :can-nudge="canNudge"
          @nudge="handleNudge"
        />
      </f7-block>

      <f7-block v-if="local.status === 'REJECTED'" class="rejection-block">
        <f7-icon f7="xmark_circle_fill" size="14" />
        {{ $t('payout.rejection', { reason: local.rejectionReason || $t('payout.rejectionDefault') }) }}
      </f7-block>

      <f7-block v-if="local.status === 'PAID'" class="paid-block">
        <f7-icon f7="checkmark_seal_fill" size="14" />
        {{ $t('payout.paidOn', {
          date: formatDate(local.paidAt),
          reference: local.paymentReference || '—',
        }) }}
      </f7-block>

      <f7-block v-if="viewerPendingApproval">
        <f7-list inset strong>
          <f7-list-input
            type="textarea"
            :label="$t('payout.notePlaceholder')"
            :value="note"
            @input="note = $event.target.value"
          />
        </f7-list>
        <div class="grid grid-clos-2">
            <f7-button large raised outline color="red" @click="handleReject">
              {{ $t('payout.reject') }}
            </f7-button>

            <f7-button large raised fill @click="handleApprove">
              {{ $t('payout.approve') }}
            </f7-button>
          </div>
        </f7-block>

      <f7-block v-if="isInitiator && local.status === 'PENDING_APPROVAL'">
        <f7-button large raised outline color="red" @click="handleCancel">
          {{ $t('payout.cancel') }}
        </f7-button>
      </f7-block>
    </f7-page>
  </AdaptiveSheet>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { f7 } from 'framework7-vue'
import i18n from '../../js/i18n.js'
import AdaptiveSheet from '../common/AdaptiveSheet.vue'
import PayoutStatusPill from './PayoutStatusPill.vue'
import PayoutApprovalRow from './PayoutApprovalRow.vue'
import { statusMeta } from '../../constants/payout-status.js'
import { usePayoutStore } from '../../js/pinia-stores/payout.js'

const props = defineProps({
  opened: { type: Boolean, default: false },
  payout: { type: Object, default: null },
  viewerId: { type: String, required: true },
  checkAuth: { type: Function, default: null },
})
const emit = defineEmits(['update:opened', 'updated'])

const payoutStore = usePayoutStore()
const local = ref(props.payout)
const note = ref('')

watch(() => props.payout, (p) => {
  local.value = p
  note.value = ''
})

const opened = computed({
  get: () => props.opened,
  set: (v) => emit('update:opened', v),
})

const status = computed(() => statusMeta(local.value?.status))
const progressPct = computed(() => {
  if (!local.value || !local.value.requiredApprovalCount) return 0
  return Math.round((local.value.currentApprovalCount / local.value.requiredApprovalCount) * 100)
})

const viewerPendingApproval = computed(() =>
  local.value?.status === 'PENDING_APPROVAL' &&
  local.value.approvals.some((a) => a.approverId === props.viewerId && a.decision === 'PENDING'))

const isInitiator = computed(() => local.value?.initiatedBy === props.viewerId)
const isApprover = computed(() =>
  local.value?.approvals.some((a) => a.approverId === props.viewerId))
const canNudge = computed(() => isInitiator.value || isApprover.value)

function fmtAmount(p) {
  return `${Number(p.amount ?? 0).toLocaleString('fr-FR')} ${p.currency || ''}`
}
function relativeTime(iso) {
  if (!iso) return ''
  const mins = Math.floor((Date.now() - new Date(iso)) / 60000)
  if (mins < 60) return `${mins} min`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h`
  return `${Math.floor(hours / 24)}j`
}
function formatDate(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })
}

async function gate(reasonKey) {
  if (typeof props.checkAuth !== 'function') return true
  return props.checkAuth({ reason: i18n.global.t(reasonKey) })
}

function apply(fresh) {
  local.value = fresh
  emit('updated', fresh)
}

async function handleApprove() {
  if (!(await gate('payout.approveReason'))) return
  f7.preloader.show()
  try {
    const fresh = await payoutStore.approve(local.value.id, props.viewerId, note.value || null)
    f7.preloader.hide()
    apply(fresh)
    f7.toast.show({ text: i18n.global.t('payout.approvedToast'), closeTimeout: 1500 })
  } catch (e) {
    f7.preloader.hide()
    f7.dialog.alert(e?.response?.data?.message || i18n.global.t('payout.actionFailed'))
  }
}

async function handleReject() {
  if (!note.value.trim()) {
    f7.dialog.alert(i18n.global.t('payout.rejectReasonRequired'))
    return
  }
  f7.dialog.confirm(i18n.global.t('payout.rejectConfirm'), async () => {
    if (!(await gate('payout.rejectReason'))) return
    f7.preloader.show()
    try {
      const fresh = await payoutStore.reject(local.value.id, props.viewerId, note.value.trim())
      f7.preloader.hide()
      apply(fresh)
      f7.toast.show({ text: i18n.global.t('payout.rejectedToast'), closeTimeout: 1500 })
    } catch (e) {
      f7.preloader.hide()
      f7.dialog.alert(e?.response?.data?.message || i18n.global.t('payout.actionFailed'))
    }
  })
}

async function handleNudge(approverId) {
  try {
    const fresh = await payoutStore.nudge(local.value.id, approverId, props.viewerId)
    apply(fresh)
    f7.toast.show({ text: i18n.global.t('payout.nudgedToast'), closeTimeout: 1500 })
  } catch (e) {
    f7.toast.show({ text: e?.response?.data?.message || i18n.global.t('payout.nudgeCooldown'), closeTimeout: 2500 })
  }
}

function handleCancel() {
  f7.dialog.confirm(i18n.global.t('payout.cancelConfirm'), async () => {
    f7.preloader.show()
    try {
      const fresh = await payoutStore.cancel(local.value.id, props.viewerId)
      f7.preloader.hide()
      apply(fresh)
      f7.toast.show({ text: i18n.global.t('payout.cancelledToast'), closeTimeout: 1500 })
    } catch (e) {
      f7.preloader.hide()
      f7.dialog.alert(e?.response?.data?.message || i18n.global.t('payout.actionFailed'))
    }
  })
}
</script>

<style scoped>
.amount-block { text-align: center; padding: 20px 16px 10px; }
.amount-value { font-size: 28px; font-weight: 500; }
.amount-recipient { font-size: 12.5px; color: var(--f7-on-surface-variant, #8e8e93); margin-top: 2px; margin-bottom: 8px; }
.reason-block { background: var(--f7-surface-container, #f5f5f5); border-radius: 10px; }
.reason-label { font-size: 10.5px; color: var(--f7-on-surface-variant, #8e8e93); text-transform: uppercase; letter-spacing: 0.04em; }
.reason-text { font-size: 13px; margin: 4px 0; }
.reason-meta { font-size: 10.5px; color: var(--f7-on-surface-variant, #8e8e93); }
.progress-block { padding-top: 0; }
.progress-track { height: 6px; border-radius: 3px; background: var(--f7-list-item-border-color, #e0e0e0); overflow: hidden; }
.progress-fill { height: 100%; background: var(--f7-theme-color); border-radius: 3px; transition: width 0.3s; }
.approval-list { background: var(--f7-surface-container, #f5f5f5); border-radius: 10px; padding: 0 8px; }
.rejection-block { display: flex; align-items: center; gap: 8px; background: var(--f7-error-container, #fcebeb); color: var(--f7-on-error-container, #a32d2d); border-radius: 8px; padding: 10px 12px; font-size: 12px; }
.paid-block { display: flex; align-items: center; gap: 8px; background: color-mix(in srgb, var(--f7-theme-color) 12%, var(--f7-card-bg-color, #fff)); color: var(--f7-theme-color); border-radius: 8px; padding: 10px 12px; font-size: 12px; }
</style>
